import { formatChatTime } from '@/constants/chat'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import type { RefObject } from 'react'
import { cn } from '@/lib/utils'
import TypingLoader from './TypingLoader'

interface MessageBubbleProps {
  text: string
  timestamp: number
  senderName: string
  senderAvatar?: string
  isOwnMessage: boolean
  isTyping?: boolean
  read?: boolean
  messageId?: string
  ref?: RefObject<HTMLDivElement>
}

export default function MessageBubble({
  text,
  timestamp,
  senderName,
  senderAvatar,
  isOwnMessage,
  isTyping = false,
  read = false,
  messageId,
  ref,
}: Readonly<MessageBubbleProps>) {
  const formattedTime = formatChatTime(timestamp)

  return (
    <div
      ref={ref}
      data-message-id={messageId}
      data-message-read={read}
      data-message-own={isOwnMessage}
      className={cn(
        'flex gap-1 mb-3',
        isOwnMessage ? 'justify-end' : 'justify-start'
      )}
    >
      {!isOwnMessage && (
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarImage src={senderAvatar} alt={senderName} />
          <AvatarFallback className="text-xs bg-ss-neutral-100 text-ss-neutral-600">
            {senderName
              .split(' ')
              .map(n => n[0])
              .join('')}
          </AvatarFallback>
        </Avatar>
      )}

      <div
        className={cn(
          'relative px-2 py-1 rounded-lg text-sm leading-relaxed min-w-32 flex flex-col max-w-2/3  ',
          isOwnMessage
            ? 'bg-ss-blue-50 text-ss-neutral-950 rounded-br-sm'
            : 'bg-ss-neutral-25 text-ss-neutral-950 rounded-bl-sm',
          isTyping && 'bg-transparent'
        )}
      >
        {isTyping ? (
          <div className="flex items-center self-start justify-center py-1 pl-1 mt-2.5 ml-1.5">
            <TypingLoader />
          </div>
        ) : (
          <p className="wrap-anywhere">{text}</p>
        )}
        {!isTyping && (
          <div className="flex justify-end items-center gap-1">
            <span
              className={cn(
                'text-[10px] font-semibold text-ss-neutral-600 leading-normal',
                isOwnMessage ? 'text-right' : 'text-left'
              )}
            >
              {formattedTime}
            </span>
            {isOwnMessage && !isTyping && (
              <span className="flex-shrink-0">
                <span
                  className={`text-xs tracking-tighter ${read ? 'text-ss-blue-500' : 'text-ss-neutral-500'}`}
                >
                  {read ? '✓✓' : ' ✓'}
                </span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
